import { withStyles, Box, Title, SubTitle, Button } from 'vopay';
import styles from './Webhooks.module.scss';
import { FunctionComponent, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import useFetchWebhookInfo from '@/hooks/useApi/useFetchWebhookInfo';
import useFetchTestWebhook from '@/hooks/useApi/useFetchTestWebhook';
import usePostUpdateWebhook from '@/hooks/useApi/usePostUpdateWebhook';
import WebhookContext, { WebhookCtx } from './Dialogue/WebhookContext';
import WebhookDialogue from './Dialogue/WebhookDialogue';
import { DialogueHandlerProps, WebhookDialogueHandler } from './Dialogue/types';
import { useClientData } from '@/providers/ClientData.provider';
import { UtilButton } from 'UI';
import { useTranslation, Trans } from 'react-i18next';

const cn = withStyles(styles);

type FC = FunctionComponent;
const Webhooks: FC = () => {
  const history = useHistory();
  const { response } = useClientData();
  const [webhookDetails, getWebhookDetails] = useFetchWebhookInfo({});
  const [testWebhookResponse, testWebhook] = useFetchTestWebhook({});
  const [updateWebhookResponse, updateWebhook] = usePostUpdateWebhook({});
  const [showDeactivateErrorAlert, setShowDeactivateErrorAlert] = useState(false);
  const [showDeactivateSuccessAlert, setShowDeactivateSuccessAlert] = useState(false);
  const [showTestErrorAlert, setShowTestErrorAlert] = useState(false);
  const [showTestSuccessAlert, setShowTestSuccessAlert] = useState(false);
  const [DeactivateError, setDeactivateError] = useState('');
  const [TestError, setTestError] = useState('');
  const [showButtons, setShowButtons] = useState(false);
  const { t } = useTranslation('translations', { keyPrefix: 'Settings.Webhooks' });
  const { t: tSetting } = useTranslation('translations', { keyPrefix: 'Settings' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const Webhook: WebhookCtx = {
    showDeactivateErrorAlert,
    showDeactivateSuccessAlert,
    showTestErrorAlert,
    showTestSuccessAlert,
    DeactivateError,
    TestError,
  };
  const handlers = {
    handleDialogue: (handlerProps: DialogueHandlerProps<WebhookDialogueHandler>) => {
      const { type, state, action = 'default' } = handlerProps;

      const dialogueHandlers: WebhookDialogueHandler = {
        test: {
          success: {
            default: () => {
              setShowTestSuccessAlert(false);
            },
          },
          error: {
            default: () => {
              setShowTestErrorAlert(false);
              setTestError('');
            },
          },
        },
        deactivate: {
          success: {
            default: () => {
              setShowDeactivateSuccessAlert(false);
            },
          },
          error: {
            default: () => {
              setShowDeactivateErrorAlert(false);
              setDeactivateError('');
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },
  };
  useEffect(() => {
    getWebhookDetails({
      PortalUser: response.LoginUserName,
    });
  }, []);
  useEffect(() => {
    if (updateWebhookResponse?.Success) {
      setShowDeactivateSuccessAlert(true);
      getWebhookDetails({
        PortalUser: response.LoginUserName,
      });
    } else if (updateWebhookResponse?.Success === false) {
      setShowDeactivateErrorAlert(true);
      setDeactivateError(updateWebhookResponse?.ErrorMessage);
    }
  }, [updateWebhookResponse]);
  useEffect(() => {
    if (testWebhookResponse?.Success) {
      setShowTestSuccessAlert(true);
    } else if (testWebhookResponse?.Success === false) {
      setShowTestErrorAlert(true);
      setTestError(testWebhookResponse?.ErrorMessage);
    }
  }, [testWebhookResponse]);
  useEffect(() => {
    if (webhookDetails?.Success) {
      setShowButtons(true);
    } else {
      setShowButtons(false);
    }
  }, [webhookDetails]);
  return (
    <WebhookContext.Provider value={Webhook}>
      <div>
        <Box mb="md" jc="sb">
          <Box flx="col" mb="md" gapSize="16px">
            <UtilButton
              iconLeft="chevron_left"
              size="large"
              onClick={() => {
                if (history.length > 1) {
                  history.goBack();
                } else {
                  history.replace('/settings');
                }
              }}
            >
              {history.length > 1 ? tGlobal('Buttons.back') : tSetting('pageTitle')}
            </UtilButton>
            <Title title={t('pageTitle')} />
          </Box>
          {webhookDetails?.WebHookURL !== '' && webhookDetails && (
            <Button
              id="decativate-webhook"
              label={t('deactivateButton')}
              onClick={() => {
                updateWebhook({
                  PortalUser: '',
                  WebHookUrl: '',
                  Disabled: true,
                });
              }}
              color="secondary"
            />
          )}
        </Box>
        <Box flx="col">
          <Box mb="md">{t('description')}</Box>

          <Box mb="md">
            <SubTitle subTitle={t('HowItWorks.title')} />
          </Box>

          <Box mb="md">
            <div>
              {t('HowItWorks.intro')}

              <ul className={cn('ul')}>
                <li>{t('HowItWorks.attempt1')}</li>
                <li>{t('HowItWorks.attempt2')}</li>
                <li>{t('HowItWorks.attempt3')}</li>
                <li>{t('HowItWorks.failed')}</li>
              </ul>
            </div>
          </Box>

          <Box mb="md">
            <SubTitle subTitle={t('TheRequest.title')} />
          </Box>

          <Box mb="md" className={cn('theRequestDescription')}>
            <Trans
              t={t}
              components={{
                a: (
                  <a
                    style={{
                      color: 'revert',
                      textDecoration: 'revert',
                    }}
                    href="https://docs.vopay.com/docs/events"
                    target="_blank"
                  />
                ),
              }}
              i18nKey="TheRequest.description"
            />
          </Box>

          <Box mt="lg" jc="fe">
            {showButtons && webhookDetails?.WebHookURL !== '' && webhookDetails && (
              <Box pe="sm">
                <Button
                  id="webhook-test"
                  color="secondary"
                  label={t('testButton')}
                  onClick={() => {
                    testWebhook({
                      PortalUser: '',
                    });
                  }}
                />
              </Box>
            )}

            <Box pe="sm">
              {showButtons && (
                <Button
                  id="webhook-update"
                  color="secondary"
                  onClick={() => {
                    history.push('/settings/webhooks/history');
                  }}
                  label={t('historyButton')}
                />
              )}
            </Box>

            <Box>
              {showButtons && (
                <Button
                  id="webhook-update"
                  onClick={() => {
                    history.push('/settings/webhooks/update');
                  }}
                  label={webhookDetails?.WebHookURL !== '' && webhookDetails ? t('manageButton') : t('activateButton')}
                />
              )}
            </Box>
          </Box>
        </Box>
      </div>

      <WebhookDialogue handleDialogue={handlers.handleDialogue} />
    </WebhookContext.Provider>
  );
};
export default Webhooks;
